import type { SVGProps } from "react";

export type AppIconName =
  | "dashboard"
  | "projects"
  | "schedule"
  | "approvals"
  | "payments"
  | "payouts"
  | "sms"
  | "reports"
  | "operations"
  | "improvements"
  | "admin"
  | "more"
  | "receivable"
  | "payable"
  | "work"
  | "attention"
  | "tasks"
  | "map"
  | "airbnb"
  | "naverBlog"
  | "folder"
  | "image"
  | "link"
  | "lock"
  | "palette"
  | "sheet"
  | "cart"
  | "images"
  | "box"
  | "pencilRuler"
  | "fileText"
  | "newspaper";

interface AppIconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: AppIconName;
}

function IconPaths({ name }: { name: AppIconName }) {
  switch (name) {
    case "images":
      return <><path d="M7 3h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /><path d="M2 7v14a1 1 0 0 0 1 1h14M5 14l4-4 7 9m-3-3 3-3 5 5" /><circle cx="16" cy="7" r="1" /></>;
    case "box":
      return <><path d="m12 3 9 5v8l-9 5-9-5V8l9-5Zm0 18V11M3 8l9 5 9-5M7.5 5.5l9 5" /></>;
    case "pencilRuler":
      return <><path d="m15 3 6 6-12 12H3v-6L15 3Zm-3 3 6 6M3 3l6 6m-3-6L3 6m12 9 6 6m-6-3 3-3" /></>;
    case "fileText":
      return <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Zm0 0v6h6M8 13h8M8 17h6" /></>;
    case "newspaper":
      return <><path d="M4 4h17v15a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8h2m0-4v15M8 8h9M8 12h3v4H8v-4Zm7 0h2m-2 4h2" /></>;

    case "dashboard":
      return (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
      );
    case "projects":
      return (
        <>
          <path d="M3 21h18" />
          <path d="M5 21V10l7-6 7 6v11" />
          <path d="M9 21v-6h6v6" />
        </>
      );
    case "schedule":
      return (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
          <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
        </>
      );
    case "approvals":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12 2.6 2.6L16.5 9" />
        </>
      );
    case "payments":
      return (
        <>
          <path d="M4 8h16v11H4z" />
          <path d="M4 11h16M8 5h8" />
          <path d="m12 4 2 2-2 2" />
        </>
      );
    case "payouts":
      return (
        <>
          <path d="M4 8h16v11H4z" />
          <path d="M4 11h16M8 5h8" />
          <path d="m12 8-2-2 2-2" />
        </>
      );
    case "sms":
      return (
        <>
          <path d="M21 15a3 3 0 0 1-3 3H8l-5 3V7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z" />
          <path d="M8 9h8M8 13h5" />
        </>
      );
    case "reports":
      return (
        <>
          <path d="M5 20V10M12 20V4M19 20v-7" />
          <path d="M3 20h18" />
        </>
      );
    case "operations":
      return (
        <>
          <path d="M4 6h7M15 6h5M4 12h3M11 12h9M4 18h9M17 18h3" />
          <circle cx="13" cy="6" r="2" />
          <circle cx="9" cy="12" r="2" />
          <circle cx="15" cy="18" r="2" />
        </>
      );
    case "improvements":
      return (
        <>
          <path d="m12 3 1.35 4.15L17.5 8.5l-4.15 1.35L12 14l-1.35-4.15L6.5 8.5l4.15-1.35z" />
          <path d="m18 14 .8 2.2 2.2.8-2.2.8L18 20l-.8-2.2L15 17l2.2-.8zM5 3l.55 1.45L7 5l-1.45.55L5 7l-.55-1.45L3 5l1.45-.55z" />
        </>
      );
    case "admin":
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15 1.7 1.7 0 0 0 3.08 14H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63 1.7 1.7 0 0 0 10 3.08V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9 1.7 1.7 0 0 0 20.92 10H21v4h-.08A1.7 1.7 0 0 0 19.4 15Z" />
        </>
      );
    case "more":
      return (
        <>
          <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case "receivable":
      return (
        <>
          <path d="M12 3v11" />
          <path d="m8 10 4 4 4-4" />
          <path d="M5 15v4h14v-4" />
        </>
      );
    case "payable":
      return (
        <>
          <path d="M12 14V3" />
          <path d="m8 7 4-4 4 4" />
          <path d="M5 15v4h14v-4" />
        </>
      );
    case "work":
      return (
        <>
          <path d="M4 19h16M6 19v-5a6 6 0 0 1 12 0v5" />
          <path d="M9 13V8.5M15 13V8.5M4 14h16" />
        </>
      );
    case "attention":
      return (
        <>
          <path d="M10.3 4.2 2.7 18a2 2 0 0 0 1.75 3h15.1a2 2 0 0 0 1.75-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </>
      );
    case "tasks":
      return (
        <>
          <rect x="4" y="5" width="16" height="16" rx="2" />
          <path d="M9 3h6v4H9zM8 13l2.3 2.3L16 9.8" />
        </>
      );
    case "map":
      return (
        <>
          <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
          <circle cx="12" cy="10" r="2.5" />
        </>
      );
    case "airbnb":
      return (
        <>
          <path d="M12 4.2c-1.7 0-2.8 2.2-4 4.8-1.5 3.2-3.1 6.8-3.1 8.4 0 1.5 1.2 2.6 2.7 2.6 1.7 0 3.3-1.4 4.4-2.6 1.1 1.2 2.7 2.6 4.4 2.6 1.5 0 2.7-1.1 2.7-2.6 0-1.6-1.6-5.2-3.1-8.4-1.2-2.6-2.3-4.8-4-4.8Z" />
          <path d="M12 10.3c-1.4 2.6-2.8 4.6-4.4 4.6-1 0-1.6-.7-1.6-1.6 0-1.1.9-2.8 2-2.8 1.5 0 2.8 1.9 4 4.1 1.2-2.2 2.5-4.1 4-4.1 1.1 0 2 1.7 2 2.8 0 .9-.6 1.6-1.6 1.6-1.6 0-3-2-4.4-4.6Z" />
        </>
      );
    case "naverBlog":
      return (
        <>
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <path d="M8 16V8l8 8V8" />
        </>
      );
    case "folder":
      return (
        <>
          <path d="M3 7.5h7l2-2h9v13H3z" />
          <path d="M3 9h18" />
        </>
      );
    case "image":
      return (
        <>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="8.5" cy="9" r="1.5" />
          <path d="m5 18 4.5-4.5 3 3 2-2L19 18" />
        </>
      );
    case "link":
      return (
        <>
          <path d="m10.5 13.5 3-3" />
          <path d="M7.8 15.8 6.4 17.2a3.4 3.4 0 0 1-4.8-4.8L5 9a3.4 3.4 0 0 1 4.8 0" />
          <path d="m16.2 8.2 1.4-1.4a3.4 3.4 0 0 1 4.8 4.8L19 15a3.4 3.4 0 0 1-4.8 0" />
        </>
      );
    case "lock":
      return (
        <>
          <rect x="5" y="10" width="14" height="11" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </>
      );
    case "palette":
      return (
        <>
          <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 0-4H12a2 2 0 0 1 0-4h4a5 5 0 0 0 5-5c0-2.8-4-5-9-5Z" />
          <circle cx="7.5" cy="9" r=".8" fill="currentColor" stroke="none" />
          <circle cx="10.5" cy="6.5" r=".8" fill="currentColor" stroke="none" />
          <circle cx="15" cy="7" r=".8" fill="currentColor" stroke="none" />
        </>
      );
    case "sheet":
      return (
        <>
          <path d="M6 3h9l3 3v15H6z" />
          <path d="M15 3v4h4M9 11h6M9 15h6" />
        </>
      );
    case "cart":
      return (
        <>
          <path d="M3 4h2l2 11h10l3-8H6" />
          <circle cx="9" cy="19" r="1.5" />
          <circle cx="17" cy="19" r="1.5" />
        </>
      );
  }
}

export function AppIcon({ name, className, ...props }: AppIconProps) {
  const classes = ["app-icon", className].filter(Boolean).join(" ");
  return (
    <svg
      {...props}
      className={classes}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <IconPaths name={name} />
    </svg>
  );
}
